import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'redux-bundler-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import { Button, Grid } from '@trussworks/react-uswds';
import { mdiCrosshairsGps } from '@mdi/js';
import { toast } from 'react-toastify';

import ErrorSummary from '@src/app-components/error-summary/ErrorSummary';
import SelectInput from '@src/app-components/new-inputs/select-input/SelectInput';
import TextInput from '@src/app-components/new-inputs/text-input/TextInput';
import TextArea from '@src/app-components/new-inputs/text-area/TextArea';
import Icon from '@src/app-components/icon/icon';
import NavigateWarningModal from '@src/common/modals/NavigateWarningModal';
import MicroBuilder from './MicroBuilder';

import {
  getMissouriRiverDefaultValues,
  getMissouriRiverSchema,
  gearReqFields,
} from './MissouriRiverDataEntryForm.validation';
import { filterNullEmptyObjects, formatCoordFlt } from '@src/utils/helpers';
import Checkbox from '@src/app-components/check-box/Checkbox';
import { useGpsCapture } from '@src/app-components/gps/gpsCapture';
import {
  calculateDistance,
  createDropdownOptions,
  currentDate,
  fmtTimeHHMMSS,
  formatGpsCoordinate,
  isEmpty,
} from '@src/app-pages/data-entry/dataEntryHelper';
import { useSharedUbloxGps } from '@src/app-pages/data-entry/offline/UbloxGpsContent';
import { captureGpsBest, GPS_OPTIONS, USE_UBLOX_POC } from '@src/app-pages/data-entry/offline/offlineHelper';
import { ApiStatuses, DataEntryStatuses, OfflineStatuses } from '@src/utils/enums';
import { createData, updateData } from '@src/app-pages/data-entry/offline/api';
import { db } from '@src/app-pages/data-entry/offline/db';
import { refreshSiteDatasheet } from '@src/app-pages/data-entry/offline/datasheet-refresh';
import { getLookupOptions } from '@src/app-pages/data-entry/offline/lookup-cache';

import '../../../dataentry.scss';

const lookupTableNames = [
  'bendRiverMile',
  'bendSelections',
  'gearCodes',
  'filteredGearCodes',
  'gearTypes',
  'macros',
  'mesos',
  'macroMesos',
  'microHabitats',
  'microStructures',
  'u6Options',
  'u7Options',
  'microSetSite',
  'setSite1Options',
  'setSite2Options',
  'setSite3Options',
  'structureFlows',
  'structureMods',
  'subsampleTypes',
];

const getDepthWarning = (depth) => {
  const warningMsg = 'Depth is greater than 10';
  return depth > 10 ? warningMsg : null;
};

const getTempWarning = (temp, gearCode) => {
  if (temp > 30) {
    return 'Temp is greater than 30';
  } else if (temp >= 12.8 && gearCode.startsWith('GN')) {
    return 'Temp >= 12.8 for a gill net gear code';
  } else {
    return null;
  }
};

const DISTANCE_ALERTS = [75, 275, 300];

const MissouriRiverDataEntryForm = connect(
  'doFetchMoRiverDataEntry',
  'doModalOpen',
  'doUpdateBaseData',
  'doAddMoRiverDataEntry',
  'doUpdateMoRiverDataEntry',
  'doUpdateCurrentTab',
  'doUpdateUrl',
  'selectBaseData',
  'selectDataEntryData',
  'selectLookupData',
  'selectRouteParams',
  'selectDataEntryFishData',
  'selectDataEntryFishTotalCount',
  'selectCurrentTab',
  'selectMoriverSitesDraftDatasheetTotalResults',
  'selectMoriverSitesDatasheetTotalResults',
  ({
    doFetchMoRiverDataEntry,
    doModalOpen,
    doUpdateBaseData,
    doAddMoRiverDataEntry,
    doUpdateMoRiverDataEntry,
    doUpdateCurrentTab,
    doUpdateUrl,
    baseData,
    dataEntryData,
    lookupData,
    routeParams,
    dataEntryFishData,
    dataEntryFishTotalCount,
    currentTab,
    moriverSitesDraftDatasheetTotalResults,
    moriverSitesDatasheetTotalResults,
    onDirtyChange,
  }) => {
    // Initialize GPS
    const browserGps = useGpsCapture(GPS_OPTIONS);
    const ubloxGps = useSharedUbloxGps();
    const liveGpsFix = USE_UBLOX_POC && ubloxGps?.isConnected ? ubloxGps?.latestFix : browserGps?.liveFix;
    const { bend, fieldoffice, season, projectId, segmentId } = baseData;
    const siteRouteKey = routeParams?.siteId;
    const isOfflineSite = String(siteRouteKey).startsWith('SITE-');
    const isOnline = navigator.onLine;
    const recoveryOutboxId = sessionStorage.getItem('syncRecoveryOutboxId');

    // Default lookups to online data, otherwise will be overwritten by offline cached lookup data if network status = offline
    const [lookups, setLookups] = useState(
      lookupTableNames.reduce((accumulator, currentKey) => {
        accumulator[currentKey] = lookupData?.[currentKey] ?? [];
        return accumulator;
      }, {})
    );
    const [gearCodeOptions, setGearCodeOptions] = useState(lookupData.gearCodes);
    const [mesoOptions, setMesoOptions] = useState(lookupData.mesos);
    const [submitMessage, setSubmitMessage] = useState(null);
    const [fishData, setFishData] = useState(dataEntryFishData?.items ?? []);

    const distanceManuallyEdited = useRef(false);
    const audioRef = useRef(null);
    const distanceAlertIndex = useRef(0);
    const [distanceTracking, setDistanceTracking] = useState(false);

    // Fetch Offline Draft Data
    const moriverDraftKey = `currentMissouriRiverDraft:${siteRouteKey}`;
    const getOfflineDraft = () => {
      const savedDraft = sessionStorage.getItem(moriverDraftKey);
      if (!savedDraft) return null;

      try {
        const draft = JSON.parse(savedDraft);
        const draftMrId = draft?.mrId ?? draft?.mr_id;
        const draftMrFid = draft?.mrFid ?? draft?.mr_fid;
        if (!draftMrId && !draftMrFid) return null;

        const checkSiteId =
          String(draft?.siteRouteKey || draft?.siteFid || draft?.site_fid || draft?.siteId) !== String(siteRouteKey);
        if (checkSiteId) return null;

        return draft;
      } catch (err) {
        console.error('Failed to parse offline Missouri River draft:', err);
        return null;
      }
    };

    const loadRecoveryMoriverDraft = async () => {
      if (!recoveryOutboxId) {
        return false;
      }

      const outboxItem = await db.outbox.get(Number(recoveryOutboxId));
      if (!outboxItem || outboxItem.tableName !== 'ds_fish') {
        return false;
      }

      const fishRecord = (await db.fish.get(outboxItem.clientId)) ?? outboxItem.payload;
      if (!fishRecord) {
        return false;
      }

      const fishMrId = fishRecord?.mrId ?? fishRecord?.mr_id;
      const fishMrFid = fishRecord?.mrFid ?? fishRecord?.mr_fid;
      const parentMoriver = await db.moriver
        .filter((moriver) => {
          const moriverMrId = moriver?.mrId ?? moriver?.mr_id;
          const moriverMrFid = moriver?.mrFid ?? moriver?.mr_fid;
          const idMatch = fishMrId != null && moriverMrId != null && String(fishMrId) === String(moriverMrId);
          const fidMatch = fishMrFid != null && moriverMrFid != null && String(fishMrFid) === String(moriverMrFid);

          return idMatch || fidMatch;
        })
        .first();

      if (!parentMoriver) {
        console.warn('Unable to load parent Missouri River draft for sync recovery:', {
          fishRecord,
          fishMrId,
          fishMrFid,
        });
        return false;
      }

      const recoveryServerMrId = Number(fishMrId) > 0 ? Number(fishMrId) : null;
      if (recoveryServerMrId) {
        await db.moriver.put({
          ...parentMoriver,
          mrId: recoveryServerMrId,
          mr_id: recoveryServerMrId,
          serverId: recoveryServerMrId,
        });
      }

      const routeMrKey = routeParams?.mrId;
      const parentMrKey = [parentMoriver?.mrId, parentMoriver?.mr_id, parentMoriver?.mrFid, parentMoriver?.mr_fid]
        .filter((value) => value !== undefined && value !== null && value !== null)
        .map(String);
      if (routeMrKey && parentMrKey.length > 0 && !parentMrKey.includes(String(routeMrKey))) {
        return false;
      }

      const fishRows = await db.fish
        .filter((fish) => {
          const rowMrId = fish?.mrId ?? fish?.mr_id;
          const rowMrFid = fish?.mrFid ?? fish?.mr_fid;
          const idMatch = fishMrId != null && rowMrId != null && String(rowMrId) === String(fishMrId);
          const fidMatch = fishMrFid && rowMrFid && String(rowMrFid) === String(fishMrFid);

          return idMatch || fidMatch;
        })
        .toArray();

      const fishCount = fishRows?.length;
      const recoveryDraft = {
        ...parentMoriver,
        ...(recoveryServerMrId
          ? {
              mrId: recoveryServerMrId,
              mr_id: recoveryServerMrId,
              serverId: recoveryServerMrId,
            }
          : {}),
        fishCount: fishCount,
      };

      reset(
        {
          ...defaultValues,
          ...recoveryDraft,
          fishCount: fishCount,
        },
        {
          keepDirty: false,
          keepTouched: false,
        }
      );
      setValue('fishCount', fishCount, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
      setFishData(fishRows);
      sessionStorage.setItem(moriverDraftKey, JSON.stringify(recoveryDraft));
      return true;
    };

    const newForm = () => {
      if (isOnline) {
        return dataEntryData?.mrId ? false : true;
      } else {
        // check draft/submitted state
        const draft = getOfflineDraft();
        return draft?.mrFid || dataEntryData?.mrFid ? false : true;
      }
    };

    const hasPDSG =
      fishData.some(
        (item) =>
          String(item?.species || '')
            .trim()
            .toUpperCase() === 'PDSG'
      ) ?? false;

    const getUpperLowerRiverMile = (bend, segment) =>
      lookups?.bendRiverMile?.filter(
        (item) => Number(item.bend) === Number(bend) && Number(item.segment) === Number(segment)
      )?.[0];

    const getSeasonGearOfficeOptions = (season, fieldOffice, project) => {
      const options = lookups?.filteredGearCodes?.filter(
        (item) => item.fieldOfficeCode === fieldOffice && item.seasonCode === season && item.projectCode === project
      );
      return options.map((item) => ({
        code: item.gearCode,
        description: item.gear,
      }));
    };

    const getMacroMesoOptions = (macro) => {
      const options = lookups?.macroMesos?.filter((item) => item.macroHabitatCode === macro);
      return options.map((item) => ({ code: item.mesoHabitatCode }));
    };

    const handleMesoOptions = useCallback(
      (mesosData, gearType, gearCode, macro, season) => {
        let options = gearType === 'S' ? getMacroMesoOptions(macro) : mesosData;
        if (gearCode === 'TN') {
          options = options.filter((item) => item.code !== 'POOL');
        }
        if (gearCode.startsWith('GN')) {
          options = options.filter((item) => item.code !== 'BARS');
        }
        if (season !== 'HW') {
          options = options.filter((item) => item.code !== 'FMCD');
        }
        setMesoOptions(options);
      },
      [getMacroMesoOptions, setMesoOptions]
    );

    const defaultValues = getMissouriRiverDefaultValues({
      baseData,
      dataEntryData,
      fishCount: isOnline ? dataEntryFishTotalCount : fishData?.length,
      moriverCount: Number(moriverSitesDraftDatasheetTotalResults) + Number(moriverSitesDatasheetTotalResults),
    });

    const fishRecordsForValidation = Number(dataEntryFishTotalCount || 0) > 0 || Number(fishData?.length || 0) > 0;

    const schema = getMissouriRiverSchema({
      riverMile: getUpperLowerRiverMile(bend, segmentId),
      hasPDSG,
      hasFishRecords: fishRecordsForValidation,
    });

    // RHF Methods Config
    const methods = useForm({
      defaultValues: defaultValues,
      resolver: yupResolver(schema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
    });
    const {
      formState: { errors, isValid, touchedFields, submitCount, isDirty },
      watch,
      getValues,
      setValue,
      handleSubmit,
      reset,
    } = methods;

    const shouldAutoValidate = submitCount > 0;

    const isTouched = Object.keys(touchedFields).length > 0;
    const isShowErrorSummary = !isValid && (isTouched || isDirty || submitCount > 0) && !isEmpty(errors);

    const deploymentType = watch('deploymentType');
    const macro = watch('macro');
    const gearCode = watch('gear');
    const gearType = watch('gearType');
    const setdate = watch('setdate');
    const subsamplepass = watch('subsamplepass');
    const netrivermile = watch('netrivermile');
    const stopTime = watch('stopTime');
    const temp = watch('temp');
    const depth1 = watch('depth1');
    const depth2 = watch('depth2');
    const depth3 = watch('depth3');
    const velocitybot1 = watch('velocitybot1');
    const velocity081 = watch('velocity081');
    const u7 = watch('u7');
    const mrFid = watch('mrFid');
    const seFid = watch('seFid');
    const startLatitude = watch('startLatitude');
    const startLongitude = watch('startLongitude');
    const stopLatitude = watch('stopLatitude');
    const stopLongitude = watch('stopLongitude');
    const distance = watch('distance');

    const isDistanceManualOnLoad = () => {
      // If is a new form and no distance value exists default autocalculate
      if (newForm() || !defaultValues?.distance) return false;

      // If there is a distance val, but all the lat/lon fields are filled out
      if (
        [
          defaultValues?.startLatitude,
          defaultValues?.startLongitude,
          defaultValues?.stopLatitude,
          defaultValues?.stopLongitude,
        ].some((v) => v === '')
      )
        return true;

      // If there is a distance val and all lat/lon fields are filled out...
      const lat1 = String(defaultValues?.startLatitude)?.replace(/[.-]/g, '') ?? '';
      const lat2 = String(defaultValues?.startLongitude)?.replace(/[.-]/g, '') ?? '';
      const lon1 = String(defaultValues?.stopLatitude)?.replace(/[.-]/g, '') ?? '';
      const lon2 = String(defaultValues?.stopLongitude)?.replace(/[.-]/g, '') ?? '';
      return Math.abs(defaultValues?.distance - calculateDistance(lat1, lat2, lon1, lon2)) > 0.001;
    };

    const fishCount = Math.max(
      Number(watch('fishCount') || 0),
      Number(dataEntryFishTotalCount || 0),
      Number(fishData?.length || 0)
    );
    const hasFishRecords = fishCount >= 1;

    const isStartTimeDisabled =
      gearCode.startsWith('LDN') &&
      (velocitybot1 === null || velocitybot1 === '' || velocity081 === null || velocity081 === '');

    const formatDataObj = () => {
      const values = getValues();
      // Format any values need for final payload
      return {
        ...values,
        bendrivermile: parseFloat(values?.bendrivermile),
        conductivity: parseFloat(values?.conductivity),
        depth1: parseFloat(values?.depth1),
        depth2: parseFloat(values?.depth2),
        depth3: parseFloat(values?.depth3),
        distance: parseFloat(values?.distance),
        startLatitude: formatCoordFlt(values.startLatitude) ?? '',
        startLongitude: formatCoordFlt(values.startLongitude) ?? '',
        stopLatitude: formatCoordFlt(values.stopLatitude) ?? '',
        stopLongitude: formatCoordFlt(values.stopLongitude) ?? '',
        subsample: parseFloat(values?.subsample),
        subsamplepass: parseFloat(values?.subsamplepass),
        temp: parseFloat(values?.temp),
        u2: String(values?.u2),
        velocitybot1: parseFloat(values?.velocitybot1),
        velocity081: parseFloat(values?.velocity081),
        velocity02or061: parseFloat(values?.velocity02or061),
        velocitybot2: parseFloat(values?.velocitybot2),
        velocity082: parseFloat(values?.velocity082),
        velocity02or062: parseFloat(values?.velocity02or062),
        width: parseFloat(values?.width),
      };
    };

    const playDistanceAlert = async () => {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/notify.wav');
          audioRef.current.preload = 'auto';
        }
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } catch (err) {
        console.error('Unable to play distance alert chime:', err);
      }
    };

    const enableDistanceAudio = () => {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio('/notify.wav');
          audioRef.current.preload = 'auto';
          audioRef.current.load();
        }
      } catch (err) {
        console.error('Unable to enable distance alert audio:', err);
      }
    };

    // Capture Start and Stop Lat, Long, Time
    const handleCapture = async (type) => {
      enableDistanceAudio();
      // When the GPS Capture buttons are clicked, reset distanceManuallyEdited ref
      distanceManuallyEdited.current = false;
      if (type === 'start') {
        distanceAlertIndex.current = 0;
        setDistanceTracking(false);
      }
      try {
        const gpsResult = await captureGpsBest({ browserGps, ubloxGps });
        if (!gpsResult) {
          throw new Error(
            'GPS did not return a location result. Confirm the GPS is connected and has a satellite fix, then try again.'
          );
        }

        const { best } = gpsResult;
        if (!best) {
          throw new Error(
            'GPS is connected but no valid location fix is available yet. Wait for satellite fix and try again.'
          );
        }

        const latitude = formatGpsCoordinate(best?.lat);
        const longitude = formatGpsCoordinate(best?.lng);

        if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
          throw new Error('GPS returned an invalid latitude or longitude. Try capturing again.');
        }
        setValue(`${type}Latitude`, latitude, { shouldValidate: shouldAutoValidate });
        setValue(`${type}Longitude`, longitude, { shouldValidate: shouldAutoValidate });
        setValue(`${type}Time`, fmtTimeHHMMSS(best.capturedAt), { shouldValidate: shouldAutoValidate });

        if (type === 'start') {
          distanceAlertIndex.current = 0;
          setValue('distance', 0, {
            shouldDirty: true,
            shouldValidate: true,
          });

          if (!(USE_UBLOX_POC && ubloxGps?.isConnected)) {
            browserGps.startWatch();
          }
          setDistanceTracking(true);
        }

        // If capturing STOP latitude and longitude, set distance
        if (type === 'stop') {
          setDistanceTracking(false);
          if (browserGps?.isWatching) {
            browserGps?.stopWatch();
          }

          if (gearCode !== 'FISH') {
            const finalDistance = calculateDistance(startLatitude, startLongitude, latitude, longitude);

            setValue('distance', finalDistance, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }

        window.alert(
          `Captured ${type === 'start' ? 'START' : 'STOP'}\nlat=${best?.lat}\nlng=${best?.lng}\nacc=${Math.round(best?.accuracy)}m`
        );
      } catch (e) {
        console.error(e);
        window.alert(`GPS capture failed: ${e?.message || e}`);
      }
    };

    const handleChange = (e) => {
      const name = e?.target?.name;
      const val = e?.target?.value;
      (name === 'recorder' || name === 'editInitials') && setValue(name, val?.toUpperCase());
    };

    const handleDistanceChange = () => {
      distanceManuallyEdited.current = true;
    };

    const doSaveDraft = async () => {
      if (!isValid) return;

      const dataObj = formatDataObj();
      const clientId = dataObj?.clientId ?? dataEntryData?.clientId ?? crypto.randomUUID();

      const mrFid = dataObj?.mrFid ?? dataObj?.mr_fid;

      const payload = filterNullEmptyObjects({
        ...dataObj,
        clientId,
        mrFid,
        mr_fid: mrFid,
        siteId: isOfflineSite ? undefined : Number(siteRouteKey),
        site_id: isOfflineSite ? undefined : Number(siteRouteKey),
        siteFid: isOfflineSite ? siteRouteKey : dataObj?.siteFid,
        site_fid: isOfflineSite ? siteRouteKey : dataObj?.site_fid,
        siteRouteKey,
        status: DataEntryStatuses.Draft,
        _status: DataEntryStatuses.Draft,
        version: dataObj.version ?? 0,
        updatedAt: new Date().toISOString(),
      });

      const finalMrId = payload?.mrId ?? payload?.mr_id;
      const finalMrFid = payload?.mrFid ?? payload?.mr_fid;

      if (!finalMrId && !finalMrFid) {
        console.error('Missing mrFid. Cannot save Missouri River draft.');
        return;
      }

      try {
        if (isOnline) {
          if (!newForm()) {
            doUpdateMoRiverDataEntry(payload);
          } else {
            const response = await doAddMoRiverDataEntry(payload);
            const savedMrId = response?.data;
            if (savedMrId) {
              payload.mrId = savedMrId;
              payload.mr_id = savedMrId;
              setValue('mrId', savedMrId);
            }
          }
        } else {
          await db.moriver.put(payload);
          toast.success('Datasheet successfully updated!');
          // Need to populate dataEntryData store
          doFetchMoRiverDataEntry({ tableId: finalMrFid }, false, false, false);
        }

        sessionStorage.setItem(moriverDraftKey, JSON.stringify(payload));

        setValue('clientId', clientId);
        setValue('mrFid', payload?.mrFid);
        setValue('status', DataEntryStatuses.Draft);
      } catch (error) {
        console.error('Save draft failed:', error);

        if (!isOnline) {
          await db.moriver.put(payload);
          sessionStorage.setItem(moriverDraftKey, JSON.stringify(payload));
          // Need to populate dataEntryData store
          doFetchMoRiverDataEntry({ tableId: finalMrFid }, false, false, false);
        }
      }
    };

    const patchRecoveryFishParent = async (recoveryItem, serverMrId) => {
      if (!recoveryItem || recoveryItem._id == null || recoveryItem.tableName !== 'ds_fish') return;
      if (!Number.isFinite(Number(serverMrId)) || Number(serverMrId) <= 0) {
        console.warn('Unable to patch recovered Fish: invalid Missouri River ID', serverMrId);
        return;
      }
      const currentPayload = recoveryItem.payload ?? {};
      const recoveryMrFid = currentPayload?.mrFid ?? currentPayload?.mr_fid;
      if (!recoveryMrFid) {
        console.warn('Unable to patch recovered Fish: missing Missouri River FID', recoveryItem);
        return;
      }

      const pendingItems = await db.outbox.toArray();
      const fishItems = pendingItems.filter((item) => {
        if (item.tableName !== 'ds_fish') {
          return false;
        }

        const payload = item.payload ?? {};
        const pendingMrFid = payload?.mrFid ?? payload?.mr_fid;
        return String(pendingMrFid) === String(recoveryMrFid);
      });

      for (const fishItem of fishItems) {
        if (fishItem._id == null) continue;
        const payload = fishItem.payload ?? {};

        await db.outbox.update(fishItem._id, {
          payload: {
            ...payload,
            mrId: Number(serverMrId),
            mr_id: Number(serverMrId),
          },
          syncError: undefined,
          syncHttp: undefined,
        });
      }

      const fishRows = await db.fish.toArray();
      const fishRowList = fishRows.filter((fish) => {
        const fishMrFid = fish?.mrFid ?? fish?.mr_fid;
        return String(fishMrFid) === String(recoveryMrFid);
      });

      for (const fishRow of fishRowList) {
        await db.fish.put({
          ...fishRow,
          mrId: Number(serverMrId),
          mr_id: Number(serverMrId),
          _status: 'queued',
        });
      }
    };

    const doSubmit = async () => {
      let fishDependencyRecovery = false;
      setValue('status', DataEntryStatuses.Submitted);

      const draft = getOfflineDraft();
      const dataObj = formatDataObj();
      const clientId = dataObj?.clientId ?? draft?.clientId ?? dataEntryData?.clientId ?? crypto.randomUUID();
      const payload = filterNullEmptyObjects({
        ...draft,
        ...dataObj,
        clientId,
        siteId: isOfflineSite ? undefined : Number(dataObj?.siteId ?? draft?.siteId ?? siteRouteKey),
        site_id: isOfflineSite ? undefined : Number(dataObj?.siteId ?? draft?.site_id ?? draft?.siteId ?? siteRouteKey),
        siteFid: isOfflineSite ? siteRouteKey : (dataObj?.siteFid ?? draft?.siteFid ?? baseData?.siteFid),
        site_fid: isOfflineSite ? siteRouteKey : (draft?.site_fid ?? baseData?.site_fid),
        siteRouteKey,
        status: DataEntryStatuses.Submitted,
        _status: OfflineStatuses.Queued,
        version: dataObj?.version ?? draft?.version ?? 0,
        updatedAt: new Date().toISOString(),
      });

      try {
        const recoveryItem = recoveryOutboxId ? await db.outbox.get(Number(recoveryOutboxId)) : null;
        fishDependencyRecovery = recoveryItem?.tableName === 'ds_fish';
        let submitMrId = Number(payload?.mrId ?? payload?.mr_id) > 0 ? Number(payload?.mrId ?? payload?.mr_id) : null;

        if (isOnline) {
          // Submit online
          const serverMrId = Number(payload?.mrId ?? payload?.mr_id ?? 0);
          const serverId = Number.isFinite(serverMrId) && serverMrId > 0;

          if (serverId) {
            const response = await doUpdateMoRiverDataEntry(payload);
            submitMrId = Number(response?.data ?? response?.mrId ?? response?.mr_id ?? serverMrId) || serverMrId;
          } else {
            const response = await doAddMoRiverDataEntry(payload);
            submitMrId = Number(response?.data ?? response?.mrId ?? response?.mr_id) || null;
            if (fishDependencyRecovery && submitMrId) {
              const localMoriver = await db.moriver.get(clientId);
              if (localMoriver) {
                await db.moriver.put({
                  ...localMoriver,
                  ...payload,
                  mrId: Number(submitMrId),
                  mr_id: Number(submitMrId),
                  status: DataEntryStatuses.Submitted,
                  _status: 'synced',
                  serverId: Number(submitMrId),
                  updatedAt: new Date().toISOString(),
                });
              }
            }
          }
          if (fishDependencyRecovery && submitMrId) {
            await patchRecoveryFishParent(recoveryItem, submitMrId);
          }
        } else {
          const serverMrId = Number(payload?.mrId ?? payload?.mr_id) > 0;
          if (serverMrId) {
            await updateData('moriver', clientId, payload);
          } else {
            await createData('moriver', payload);
          }
          toast.success('Datasheet successfully updated!');
          doFetchMoRiverDataEntry(
            {
              tableId: payload?.mrFid ?? payload?.mr_fid,
            },
            false,
            false,
            false
          );
        }

        if (isOnline && fishDependencyRecovery && !submitMrId) {
          throw new Error(
            'Missouri River form was submitted, but no server Missouri River ID was returned. Fish cannot be linked yet.'
          );
        }
        setValue('clientId', clientId);
        setValue('status', DataEntryStatuses.Submitted);
        if (submitMrId) {
          setValue('mrId', submitMrId);
        }
        sessionStorage.removeItem(moriverDraftKey);
        refreshSiteDatasheet();

        setSubmitMessage({
          type: ApiStatuses.Success,
          text: fishDependencyRecovery
            ? 'Missouri River submitted successfully. Press Sync button again to submit the Fish record again..'
            : isOnline
              ? 'Missouri River form submitted successfully.'
              : 'Missouri River form saved offline successfully. It will sync when you are back online.',
        });
      } catch (error) {
        console.error('Submit failed, queueing offline:', error);
        setSubmitMessage({
          type: ApiStatuses.Failed,
          text: error?.message || 'Missouri River could not be submitted. Please try again',
        });
      }
    };

    const handleSaveAndClose = (isSubmit = false) => {
      // Save/Submit Form
      isSubmit ? doSubmit() : doSaveDraft();
      // Navigate to Fish Data Entry Form Tab
      doUpdateUrl(`/sites-list/${siteRouteKey}`);
    };

    const handleSaveAndFish = (isSubmit = false) => {
      // Save/Submit Form
      isSubmit ? doSubmit() : doSaveDraft();
      // Navigate to Fish Data Entry Form Tab
      doUpdateCurrentTab(1);
    };

    const handleClose = () => doModalOpen(NavigateWarningModal, { url: `/sites-list/${siteRouteKey}` });

    const reloadOfflineDraft = () => {
      reset(
        {
          ...defaultValues,
          fishCount: Number(dataEntryFishTotalCount || fishData?.length || 0),
        },
        {
          keepDirty: false,
          keepTouched: false,
        }
      );
      return true;
    };

    useEffect(() => {
      reloadOfflineDraft();
    }, [dataEntryFishTotalCount, currentTab]);

    // Set R/N value
    useEffect(() => {
      subsamplepass && u7 && setValue('subsamplen', subsamplepass > 1 || u7 === 'BS' ? 'N' : 'R');
    }, [subsamplepass]);

    // Set Gear Code options and reset Gear Code value when necessary
    useEffect(() => {
      setGearCodeOptions(
        gearType === 'S' ? getSeasonGearOfficeOptions(season, fieldoffice, projectId) : lookups?.gearCodes
      );

      if (recoveryOutboxId) return;
      setValue('gear', '');
    }, [gearType, recoveryOutboxId]);

    // Set Meso options and reset Meso value when necessary
    useEffect(() => {
      handleMesoOptions(lookups?.mesos, gearType, gearCode, macro, season);
    }, [lookups?.mesos, gearType, gearCode, macro, season]);

    // Both Velocity (bot) 1 and Velocity (0.8 or 0.5) 1 must be filled out before setting the start time when using a Larval Drift Net gear.
    useEffect(() => {
      if (gearCode.startsWith('LDN') && velocitybot1 === null && velocity081 === null) {
        // Reset start time under these conditions
        setValue('startTime', '', { shouldValidate: shouldAutoValidate });
      }
    }, [gearCode, velocitybot1, velocity081, shouldAutoValidate]);

    useEffect(() => {
      if (gearCode) {
        setValue('deploymentType', lookups?.gearCodes?.filter((gear) => gear.code === gearCode)?.[0]?.deploymentType);
        if (newForm()) {
          if (gearCode === 'TLC1') {
            setValue('u2', 20);
          }
          if (gearCode === 'TLC2') {
            setValue('u2', 40);
          }
        }
      }
    }, [gearCode]);

    // Populate Gear Code Dropdown Value from Existing API Data
    useEffect(() => {
      if (gearCodeOptions.length === 0) return;
      const draft = getOfflineDraft();
      const recoveryGear = recoveryOutboxId ? draft?.gear : dataEntryData?.gear;
      if (recoveryGear) {
        setValue('gear', recoveryGear, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }, [dataEntryData?.gear, gearCodeOptions.length, recoveryOutboxId, setValue]);

    // Populate Meso Dropdown Value from Existing API Data
    useEffect(() => {
      if (mesoOptions.length === 0) return;
      const draft = getOfflineDraft();
      const mesoVal = recoveryOutboxId ? draft?.meso : dataEntryData?.meso;
      setValue('meso', mesoVal);
    }, [dataEntryData?.meso, mesoOptions.length, recoveryOutboxId, setValue]);

    // netrivermile in baseData
    useEffect(() => {
      doUpdateBaseData('netrivermile', netrivermile);
    }, [netrivermile]);

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Get Offline Fish Data
    useEffect(() => {
      const populateOfflineFishData = async (id) => {
        const cachedData = await db.fish.toArray();
        // Determine whether to search via Table ID or Field ID
        const filteredCachedData = cachedData.filter((item) => {
          const itemMrFid = item?.mrFid ?? item?.mr_fid;
          const itemMrId = item?.mrId ?? item?.mr_id;

          return String(itemMrFid ?? '') === String(id ?? '') || String(itemMrId ?? '') === String(id ?? '');
        });
        setFishData(filteredCachedData);
      };

      if (recoveryOutboxId) return;
      // Only run when offline in offline status and mrFid exists
      !isOnline && mrFid !== '' && void populateOfflineFishData(mrFid);
      isOnline && setFishData(dataEntryFishData?.items ?? []);
    }, [mrFid, isOnline, currentTab, dataEntryFishData, recoveryOutboxId]);

    // Load offline lookups
    useEffect(() => {
      const loadOfflineLookups = async () => {
        const entries = await Promise.all(lookupTableNames.map(async (name) => [name, await getLookupOptions(name)]));
        const cachedLookups = Object.fromEntries(entries);
        setLookups((current) => {
          const merged = {
            ...current,
          };

          Object.entries(cachedLookups).forEach(([name, options]) => {
            if (Array.isArray(options) && options.length > 0) {
              merged[name] = options;
            }
          });
          return merged;
        });
      };
      if (!isOnline || recoveryOutboxId) {
        void loadOfflineLookups();
      }
    }, [isOnline, recoveryOutboxId]);

    useEffect(() => {
      if (!recoveryOutboxId) return;
      void loadRecoveryMoriverDraft();
    }, [recoveryOutboxId, routeParams?.mrId]);

    useEffect(() => {
      if (recoveryOutboxId) return;
      const count = Number(dataEntryFishTotalCount || 0);

      setValue('fishCount', count, { shouldValidate: true, shouldDirty: false, shouldTouch: false });
    }, [dataEntryFishTotalCount, setValue, recoveryOutboxId]);

    // Calculate Distance when start and stop lat/lon changes
    useEffect(() => {
      // The system shall NOT autocalculate when gear = 'FISH' and when the Distance value was manuallyu edited
      if (distanceManuallyEdited.current || gearCode === 'FISH' || isDistanceManualOnLoad()) return;
      // Make sure all start and stop lat/lon values are filled in before calculating, otherwise reset Distance value
      const coordinates = [startLatitude, startLongitude, stopLatitude, stopLongitude];
      const latLonHasEmpty = coordinates.some((v) => v === '');
      const distanceVal = !latLonHasEmpty
        ? calculateDistance(startLatitude, startLongitude, stopLatitude, stopLongitude)
        : '';
      setValue('distance', distanceVal);
    }, [gearCode, startLatitude, startLongitude, stopLatitude, stopLongitude]);

    useEffect(() => {
      if (!distanceTracking) return;
      if (distanceManuallyEdited.current) return;
      if (gearCode === 'FISH') return;
      if (!liveGpsFix) return;

      const currentLat = Number(liveGpsFix.lat);
      const currentLng = Number(liveGpsFix.lng);
      const startLat = Number(startLatitude);
      const startLng = Number(startLongitude);

      if (
        !Number.isFinite(currentLat) ||
        !Number.isFinite(currentLng) ||
        !Number.isFinite(startLat) ||
        !Number.isFinite(startLng)
      ) {
        return;
      }

      const currentDistance = calculateDistance(startLat, startLng, currentLat, currentLng);
      setValue('distance', currentDistance, {
        shouldDirty: true,
        shouldValidate: false,
      });

      const nextAlertDistance = DISTANCE_ALERTS[distanceAlertIndex.current];
      if (nextAlertDistance !== undefined && currentDistance >= nextAlertDistance) {
        void playDistanceAlert();
        console.log(`Distance alert: reached ${nextAlertDistance} meters`);
        distanceAlertIndex.current += 1;
      }
    }, [distanceTracking, liveGpsFix, startLatitude, startLongitude, gearCode, setValue]);

    useEffect(() => {
      return () => {
        browserGps.stopWatch();
      };
    }, [browserGps.stopWatch]);

    return (
      <FormProvider {...methods}>
        {isShowErrorSummary && <ErrorSummary errors={errors} type='form' isValid={isValid} />}
        {submitMessage && (
          <div className={`usa-alert usa-alert--${submitMessage.type}`} role='status'>
            <div className='usa-alert__body'>
              <p className='usa-alert__text'>{submitMessage.text}</p>
            </div>
          </div>
        )}
        <div className='container-fluid test-moriver margin-top-1'>
          <Grid row gap='md'>
            <Grid desktop={{ col: 2 }}>
              <p>
                MR Field ID (Date-Time-SE#):<br></br>
                <span className='text-bold'>{mrFid}</span>
              </p>
            </Grid>
            <Grid desktop={{ col: 2 }}>
              <p>
                SE Field ID (Date-Time-SE#):<br></br>
                <span className='text-bold'>{seFid !== '' ? seFid : '--'}</span>
              </p>
            </Grid>
            <Grid desktop={{ col: 8 }}>
              {!hasFishRecords ? (
                <Button className='add-btn save-btn' onClick={handleSubmit(doSaveDraft)} type='button'>
                  Save as Draft
                </Button>
              ) : (
                <Button className='add-btn save-btn' onClick={handleSubmit(doSubmit)} type='button'>
                  Submit
                </Button>
              )}
              <Button
                className='add-btn save-btn'
                onClick={handleSubmit(() => handleSaveAndClose(hasFishRecords))}
                type='button'
              >
                Save & Close
              </Button>
              <Button
                className='add-btn save-btn'
                onClick={handleSubmit(() => handleSaveAndFish(hasFishRecords))}
                type='button'
              >
                Save & Open Fish Datasheet
              </Button>
              <Button className='close-btn save-btn' onClick={handleClose} type='button'>
                Close
              </Button>
            </Grid>
          </Grid>

          <Grid row gap='md'>
            <Grid desktop={{ col: 2 }} tablet={{ col: 4 }}>
              <TextInput
                name='setdate'
                label='Set Date'
                type='date'
                onChange={handleChange}
                disabled={deploymentType === 'p' && setdate !== currentDate}
                required
              />
            </Grid>
            <Grid desktop={{ col: 1 }} tablet={{ col: 2 }}>
              <TextInput name='subsample' label='Subsample' type='number' onChange={handleChange} required />
            </Grid>
            <Grid desktop={{ col: 1 }} tablet={{ col: 2 }}>
              <TextInput name='subsamplepass' label='Pass' type='number' onChange={handleChange} required />
            </Grid>
            <Grid desktop={{ col: 2 }} tablet={{ col: 4 }}>
              <SelectInput name='subsamplen' label='Subsample R/N' onChange={handleChange} required>
                {createDropdownOptions(lookups?.bendSelections).map((item, index) => (
                  <option key={index + 1} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </SelectInput>
            </Grid>
            <Grid desktop={{ col: 2 }} tablet={{ col: 4 }}>
              <SelectInput name='subsampleType' label='Subsample Type' onChange={handleChange} required>
                {createDropdownOptions(lookups?.subsampleTypes).map((item, index) => (
                  <option key={index + 1} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </SelectInput>
            </Grid>
            <Grid desktop={{ col: 1 }} tablet={{ col: 2 }}>
              <TextInput
                name='recorder'
                label='Recorder'
                maxLength={3}
                onChange={handleChange}
                style={{ textTransform: 'uppercase' }}
                required
              />
            </Grid>
          </Grid>
          <Grid row gap='md' className='padding-bottom-3'>
            <Grid desktop={{ col: 2 }} tablet={{ col: 4 }}>
              <SelectInput name='gearType' label='Gear Type' onChange={handleChange} required>
                {createDropdownOptions(lookups?.gearTypes).map((item, index) => (
                  <option key={index + 1} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </SelectInput>
            </Grid>
            <Grid desktop={{ col: 2 }} tablet={{ col: 4 }}>
              <SelectInput name='gear' label='Gear Code' onChange={handleChange} required>
                {createDropdownOptions(gearCodeOptions).map((item, index) => (
                  <option key={index + 1} value={item.value}>
                    {item.value}
                  </option>
                ))}
              </SelectInput>
            </Grid>
          </Grid>

          <Grid row gap='md' className='padding-bottom-3'>
            <Grid desktop={{ col: 4 }} tablet={{ col: 12 }} className='border-right'>
              <Grid row gap='md'>
                <Grid desktop={{ col: 6 }} tablet={{ col: 6 }}>
                  <SelectInput name='macro' label='Macro' onChange={handleChange} required>
                    {createDropdownOptions(lookups?.macros).map((item, index) => (
                      <option key={index + 1} value={item.value}>
                        {item.value}
                      </option>
                    ))}
                  </SelectInput>
                </Grid>
                <Grid desktop={{ col: 6 }} tablet={{ col: 6 }}>
                  <SelectInput name='meso' label='Meso' onChange={handleChange} required>
                    {createDropdownOptions(mesoOptions).map((item, index) => (
                      <option key={index + 1} value={item.value}>
                        {item.value}
                      </option>
                    ))}
                  </SelectInput>
                </Grid>
              </Grid>
              <Grid row gap='md'>
                <Grid desktop={{ col: 6 }} tablet={{ col: 6 }}>
                  <TextInput
                    name='temp'
                    label='Temp (c)'
                    type='number'
                    onChange={handleChange}
                    required
                    warning={getTempWarning(temp, gearCode)}
                  />
                </Grid>
                <Grid desktop={{ col: 6 }} tablet={{ col: 6 }}>
                  <TextInput
                    name='width'
                    label='Width'
                    type='number'
                    onChange={handleChange}
                    isMuted={gearType === 'S'}
                  />
                </Grid>
              </Grid>
            </Grid>

            <MicroBuilder shouldAutoValidate={shouldAutoValidate} />
          </Grid>

          <Grid row gap='md' className='padding-bottom-3'>
            <Grid desktop={{ col: 5 }} tablet={{ col: 12 }} className='border-right'>
              <Grid row gap='md'>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='startTime'
                    label='Start Time'
                    maxLength={11}
                    onChange={handleChange}
                    required={!isStartTimeDisabled}
                    readOnly={isStartTimeDisabled}
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='startLatitude'
                    label='Start Latitude'
                    type='number'
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='startLongitude'
                    label='Start Longitude'
                    type='number'
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid row gap='md' tablet={{ col: 3 }}>
                  <Button
                    onClick={() => handleCapture('start')}
                    type='button'
                    className='primary-btn margin-left-1 margin-top-1'
                  >
                    <Icon path={mdiCrosshairsGps} className='margin-right-1' />
                    Capture Start GPS
                  </Button>
                </Grid>
              </Grid>
              <Grid row gap='md'>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput
                    name='distance'
                    label='Distance (m)'
                    type='number'
                    onChange={handleDistanceChange}
                    required={
                      gearType === 'S' && gearReqFields.distance.includes(gearCode) && hasFishRecords ? true : false
                    }
                    isMuted={
                      gearType === 'S' && gearReqFields.distance.includes(gearCode) && hasFishRecords ? false : true
                    }
                  />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput
                    name='depth1'
                    label='1-Depth (m)'
                    onChange={handleChange}
                    required={gearType === 'S' && gearReqFields.depth1.includes(gearCode) ? true : false}
                    isMuted={gearType === 'S' && gearReqFields.depth1.includes(gearCode) ? false : true}
                    type='number'
                    warning={getDepthWarning(depth1)}
                  />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput
                    name='depth2'
                    label='2-Depth (m)'
                    onChange={handleChange}
                    required={gearType === 'S' && gearReqFields.depth2.includes(gearCode) ? true : false}
                    isMuted={gearType === 'S' && gearReqFields.depth2.includes(gearCode) ? false : true}
                    type='number'
                    warning={getDepthWarning(depth2)}
                  />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput
                    name='depth3'
                    label='3-Depth (m)'
                    onChange={handleChange}
                    required={gearType === 'S' && gearReqFields.depth3.includes(gearCode) ? true : false}
                    isMuted={gearType === 'S' && gearReqFields.depth3.includes(gearCode) ? false : true}
                    type='number'
                    warning={getDepthWarning(depth3)}
                  />
                </Grid>
              </Grid>
              <Grid row gap='md'>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='stopTime'
                    label='Stop Time'
                    maxLength={11}
                    onChange={handleChange}
                    warning={
                      deploymentType === 'p' && (stopTime === null || stopTime === '') && !hasFishRecords
                        ? 'Deployment type = p but the stop time is not filled in'
                        : null
                    }
                    required={deploymentType === 'p' && hasFishRecords}
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='stopLatitude'
                    label='Stop Latitude'
                    onChange={handleChange}
                    required={deploymentType === 'a' && !gearCode.startsWith('LDN')}
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='stopLongitude'
                    label='Stop Longitude'
                    onChange={handleChange}
                    required={deploymentType === 'a' && !gearCode.startsWith('LDN')}
                  />
                </Grid>
                <Grid row gap='md' tablet={{ col: 3 }}>
                  <Button
                    onClick={() => handleCapture('stop')}
                    type='button'
                    className='primary-btn margin-left-1 margin-top-1'
                  >
                    <Icon path={mdiCrosshairsGps} className='margin-right-1' />
                    Capture Stop GPS
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid desktop={{ col: 7 }}>
              <Grid row gap='md'>
                <Grid desktop={{ col: 2 }} tablet={{ col: 2 }}>
                  <TextInput
                    name='u1'
                    label='U1'
                    maxLength={3}
                    required={Number(projectId) === 3}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid desktop={{ col: 2 }} tablet={{ col: 2 }}>
                  <TextInput
                    name='u2'
                    label='U2'
                    maxLength={3}
                    onChange={handleChange}
                    required={Number(projectId) === 1 && gearCode.startsWith('TL')}
                    type={gearCode.startsWith('TL') ? 'number' : 'text'}
                  />
                </Grid>
                <Grid desktop={{ col: 2 }} tablet={{ col: 2 }}>
                  <TextInput name='u3' label='U3' maxLength={4} onChange={handleChange} />
                </Grid>
                <Grid desktop={{ col: 2 }} tablet={{ col: 2 }}>
                  <TextInput name='u4' label='U4' maxLength={4} onChange={handleChange} />
                </Grid>
                <Grid desktop={{ col: 2 }} tablet={{ col: 2 }}>
                  <TextInput name='u5' label='U5' maxLength={6} onChange={handleChange} />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <SelectInput name='u6' label='U6' onChange={handleChange}>
                    {lookups?.u6Options?.map((item, index) => (
                      <option key={index + 1} value={item.code}>
                        {item.description}
                      </option>
                    ))}
                  </SelectInput>
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <SelectInput name='u7' label='U7' onChange={handleChange}>
                    {createDropdownOptions(lookups?.u7Options).map((item, index) => (
                      <option key={index + 1} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </SelectInput>
                </Grid>
              </Grid>
              <Grid row gap='md'>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput
                    name='structurenumber'
                    label='Structure Number'
                    maxLength={50}
                    onChange={handleChange}
                    required={Number(projectId) === 2 && season === 'HS'}
                  />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput name='netrivermile' label='Net River Mile' onChange={handleChange} />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput name='conductivity' label='Conductivity' onChange={handleChange} />
                </Grid>
                <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                  <TextInput name='dissolvedOxygen' label='Dissolved Oxygen (D.O.)' onChange={handleChange} />
                </Grid>
              </Grid>
              {isOnline && (
                <Grid row gap='md'>
                  <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                    <TextInput name='usgs' label='USGS' maxLength={8} isMuted />
                  </Grid>
                  <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                    <TextInput name='riverstage' label='River Stage' isMuted />
                  </Grid>
                  <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                    <TextInput name='discharge' label='Discharge' isMuted />
                  </Grid>
                  <Grid desktop={{ col: 3 }} tablet={{ col: 3 }}>
                    <TextInput name='habitatrn' label='Habitat R/N' maxLength={1} isMuted />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid row gap='md' className='padding-bottom-3'>
            <Grid desktop={{ col: 4 }} tablet={{ col: 12 }} className='border-right'>
              <Grid row gap='md'>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='velocitybot1'
                    label='1-Velocity (bot)'
                    onChange={handleChange}
                    required={
                      hasPDSG && gearType === 'S' && gearReqFields.velocitybot1.includes(gearCode) ? true : false
                    }
                    isMuted={
                      hasPDSG && gearType === 'S' && gearReqFields.velocitybot1.includes(gearCode) ? false : true
                    }
                    type='number'
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='velocity081'
                    label='1-Velocity (0.8 or 0.5)'
                    onChange={handleChange}
                    required={
                      hasPDSG &&
                      gearType === 'S' &&
                      gearReqFields.velocity081.includes(gearCode) &&
                      Number(depth2) >= 1.2
                        ? true
                        : false
                    }
                    isMuted={
                      hasPDSG &&
                      gearType === 'S' &&
                      gearReqFields.velocity081.includes(gearCode) &&
                      Number(depth2) >= 1.2
                        ? false
                        : true
                    }
                    type='number'
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='velocity02or061'
                    label='1-Velocity (0.2 or 0.6)'
                    type='number'
                    onChange={handleChange}
                    isMuted={true}
                  />
                </Grid>
              </Grid>
              <Grid row gap='md'>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='velocitybot2'
                    label='2-Velocity (bot)'
                    onChange={handleChange}
                    required={
                      hasPDSG && gearType === 'S' && gearReqFields.velocitybot2.includes(gearCode) ? true : false
                    }
                    isMuted={
                      hasPDSG && gearType === 'S' && gearReqFields.velocitybot2.includes(gearCode) ? false : true
                    }
                    type='number'
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='velocity082'
                    label='2-Velocity (0.8 or 0.5)'
                    onChange={handleChange}
                    required={
                      hasPDSG &&
                      gearType === 'S' &&
                      gearReqFields.velocity082.includes(gearCode) &&
                      Number(depth2) >= 1.2
                        ? true
                        : false
                    }
                    isMuted={
                      hasPDSG &&
                      gearType === 'S' &&
                      gearReqFields.velocity082.includes(gearCode) &&
                      Number(depth2) >= 1.2
                        ? false
                        : true
                    }
                    type='number'
                  />
                </Grid>
                <Grid desktop={{ col: 4 }} tablet={{ col: 4 }}>
                  <TextInput
                    name='velocity02or062'
                    label='2-Velocity (0.2 or 0.6)'
                    onChange={handleChange}
                    required={
                      hasPDSG && gearType === 'S' && gearReqFields.velocity02or062.includes(gearCode) ? true : false
                    }
                    isMuted={
                      hasPDSG && gearType === 'S' && gearReqFields.velocity02or062.includes(gearCode) ? false : true
                    }
                    type='number'
                  />
                </Grid>
              </Grid>
            </Grid>
            {isOnline && (
              <Grid desktop={{ col: 4 }} tablet={{ col: 6 }}>
                <Grid row gap='md'>
                  <Grid desktop={{ col: 5 }} tablet={{ col: 6 }}>
                    <TextInput name='cobble' label='Cobble' isMuted />
                  </Grid>
                  <Grid desktop={{ col: 5 }} tablet={{ col: 6 }}>
                    <TextInput name='silt' label='Silt' isMuted />
                  </Grid>
                </Grid>
                <Grid row gap='md'>
                  <Grid desktop={{ col: 5 }} tablet={{ col: 6 }}>
                    <TextInput name='organic' label='Organic' isMuted />
                  </Grid>
                  <Grid desktop={{ col: 5 }} tablet={{ col: 6 }}>
                    <TextInput name='sand' label='Sand' isMuted />
                  </Grid>
                </Grid>
                <Grid row gap='md'>
                  <Grid desktop={{ col: 5 }} tablet={{ col: 6 }}>
                    <TextInput name='gravel' label='Gravel' isMuted />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid desktop={{ col: 2 }} tablet={{ col: 6 }}>
              <Grid row gap='md'>
                <Grid desktop={{ col: 6 }} tablet={{ col: 6 }}>
                  <TextInput name='turbidity' label='Turbidity' type='number' onChange={handleChange} />
                </Grid>
              </Grid>
              {/* @TODO: Hide No Turbidity and No Velocity fields in Offline Mode */}
              <Grid row gap='md' className='margin-top-2'>
                <Grid desktop={{ col: 12 }} tablet={{ col: 6 }}>
                  <Checkbox disabled id='no-turbidity' label='No Turbidity' name='noTurbidity' tile value='Y' />
                </Grid>
              </Grid>
              <Grid row gap='md'>
                <Grid desktop={{ col: 12 }} tablet={{ col: 6 }}>
                  <Checkbox disabled id='no-velocity' label='No Velocity' name='noVelocity' tile value='Y' />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {isOnline && (
            <Grid row gap='md' className='padding-bottom-3'>
              <Grid desktop={{ col: 6 }} tablet={{ col: 12 }}>
                <Grid row gap='md'>
                  <Grid desktop={{ col: 12 }}>
                    <TextArea name='comments' label='Comments' maxLength={2000} onChange={handleChange} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid desktop={{ col: 1 }} tablet={{ col: 2 }}>
                <Grid row gap='md'>
                  <Grid desktop={{ col: 12 }}>
                    <TextInput name='editInitials' label='Edit Initials' maxLength={3} onChange={handleChange} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </div>
      </FormProvider>
    );
  }
);

export default MissouriRiverDataEntryForm;
